import { useCallback, useState } from 'react';
import { get, isEmpty, set, cloneDeep } from 'lodash';
import { commonPath } from '../fieldPath';
import { checkArray } from '../../../../Components/helper';
import type {
  AdditionalComponentState,
  AdditionalState,
  DetailedCostingState,
  DetailedQuantitiesState,
  GlobalError,
  ProductCostingState,
  TestingState,
} from '../types';

/** Per-section list of the field keys that must carry a value before a costing can be saved. */
type ValidateFieldMap = Record<string, string[]>;

/** Anything indexable by a validated field key — every validated row shape reduces to this. */
type ValidatableRow = Record<string, unknown>;

/* Hoisted to module scope so `checkValidation` can be a stable useCallback. Recreating these inside
   the component would put a fresh identity in the callback's dependency list on every render, which
   is what defeated the context provider's useMemo in the original (B1). VALIDATE_FIELDS is only ever
   read — the `additional` branches build a new array rather than pushing into it. */
const VALIDATE_FIELDS: ValidateFieldMap = {
  productInfo: ['supplierId', 'currency', 'incoterms'],
  averageCosting: ['quantityPerSizCol'],
  detailedCostingErr: ['quantityUnitOfMeasure', 'quantityPerSizCol', 'pricePerUnit'],
  testing: ['colourway'],
  quantities: ['quantityPerSizCol'],
  additional: ['quantityPerColSizLen', 'pricePerUoM', 'totalPrice', 'quantityUom'],
  additionalComponent: ['quantityPerColSizLen', 'pricePerUoM', 'totalPrice', 'quantityUom'],
  productTemplate: ['productTemplate'],
};

const THIS_FIELD_IS_REQUIRED = 'This field is required';

/** True when a value counts as "not filled in": nullish, blank, `{}` or `[]`. */
const isBlankValue = (value: unknown): boolean => {
  const isEmptyObject = Boolean(value) && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0;
  const isEmptyArray = Array.isArray(value) && value.length === 0;
  return value === undefined || value === null || value === '' || isEmptyObject || isEmptyArray;
};

/** True when a value is falsy, or is an object with no keys. Looser than isBlankValue: `0` fails. */
const isFalsyOrEmptyObject = (value: unknown): boolean => !value || (typeof value === 'object' && Object.keys(value as object).length === 0);

export interface UseValidationHooksResult {
  checkValidation: (isColourHidden?: boolean) => boolean;
  setGlobalErrors: React.Dispatch<React.SetStateAction<GlobalError>>;
  globalError: GlobalError;
  onChangeCheckIfErrorExist: (path: string, errorsData: GlobalError) => void;
}

/**
 * Required-field validation for the costing detail page.
 *
 * Errors are written into a single flat object keyed by the same bracketed paths that
 * `errorpath()` produces, so a table can look up its own cell's error without knowing how the
 * validator walked the tree.
 *
 * Every parameter is optional because most tables call `useValidationHooks()` with no arguments —
 * they only want `onChangeCheckIfErrorExist`, and never invoke `checkValidation`. Only the context
 * provider passes the full set. This mirrors the original, where the same call was untyped.
 *
 * @returns the validator, the accumulated errors and the two setters the tables use.
 */
export default function useValidationHooks(
  productCostingstate?: ProductCostingState,
  additionalState?: AdditionalState,
  testingState?: TestingState,
  detailedCostingState?: DetailedCostingState,
  detailedQuanitiesState?: DetailedQuantitiesState,
  colourWayToggle?: boolean,
  _sizeLengthToggle?: boolean,
  additionalComponentState?: AdditionalComponentState,
  hideColourCol?: boolean,
): UseValidationHooksResult {
  const [globalError, setGlobalErrors] = useState<GlobalError>({});

  /* `_isColourHidden` is declared by the original signature and never read in the body. The
     underscore keeps it on the arity for existing call sites without tripping noUnusedParameters.

     Wrapped in useCallback so the identity only changes when the validated state actually changes.
     The original rebuilt this function on every render, and because the context provider lists it
     in its useMemo dependencies, that alone defeated the memo (B1). */
  const checkValidation = useCallback((_isColourHidden?: boolean): boolean => {
    /* Each clone is annotated because @types/lodash is not installed, so `cloneDeep` resolves to
       `any` — without these the whole validation walk would silently lose its types. */
    const fieldError: GlobalError = cloneDeep(globalError);
    const copyState: ProductCostingState = cloneDeep(productCostingstate);
    const copyAdditionalState: AdditionalState = cloneDeep(additionalState);
    const copyAdditionalComponentState: AdditionalComponentState = cloneDeep(additionalComponentState);
    const copyTestigState: TestingState = cloneDeep(testingState);
    const copyDetailedState: DetailedCostingState = cloneDeep(detailedCostingState);
    const copyQuantitiesState: DetailedQuantitiesState = cloneDeep(detailedQuanitiesState);
    let hasErrors = false;

    Object.keys(VALIDATE_FIELDS)?.forEach((key) => {
      const fields = VALIDATE_FIELDS[key] ?? [];
      switch (key) {
        case 'productInfo': {
          const productInfoData = get(copyState, commonPath().PRODUCT_INFO) as ValidatableRow;
          fields.forEach((fieldKey) => {
            if (!productInfoData[fieldKey]) {
              hasErrors = true;
              set(fieldError, `[${key}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
            } else set(fieldError, `[${key}][${fieldKey}]`, '');
          });
          break;
        }
        case 'testing': {
          const testingData = copyTestigState?.testing?.testingInfo || [];

          if (colourWayToggle) {
            testingData?.forEach((element, index) => {
              fields.forEach((fieldKey) => {
                if (!element[fieldKey]) {
                  hasErrors = true;
                  set(fieldError, `[${key}][${index}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
                } else set(fieldError, `[${key}][${index}][${fieldKey}]`, '');
              });
            });
          }
          break;
        }
        case 'additional': {
          const isActive = copyState?.productTemplate?.isSetUpCostActive;
          const additionalData = copyAdditionalState?.additional?.additionalInfo || [];
          if (isActive && additionalData.length > 0) {
            const requiredFields = hideColourCol ? [...fields, 'colourway'] : fields;
            additionalData.forEach((element, index) => {
              if (!element?.title) {
                requiredFields.forEach((fieldKey) => set(fieldError, `[${key}][${index}][${fieldKey}]`, ''));
                return;
              }
              requiredFields.forEach((fieldKey) => {
                if (isBlankValue(element[fieldKey])) {
                  hasErrors = true;
                  set(fieldError, `[${key}][${index}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
                } else set(fieldError, `[${key}][${index}][${fieldKey}]`, '');
              });
            });
          }
          break;
        }
        case 'additionalComponent': {
          const isActive = copyState?.productTemplate?.isAdditionalComponentActive;
          const additionalComponentData = copyAdditionalComponentState?.additionalComponent?.additionalComponentInfo || [];
          if (isActive && additionalComponentData.length > 0) {
            const requiredFields = hideColourCol ? [...fields, 'colourway'] : fields;
            additionalComponentData.forEach((element, index) => {
              if (!element?.title) {
                requiredFields.forEach((fieldKey) => set(fieldError, `[${key}][${index}][${fieldKey}]`, ''));
                return;
              }
              requiredFields.forEach((fieldKey) => {
                if (isBlankValue(element[fieldKey])) {
                  hasErrors = true;
                  set(fieldError, `[${key}][${index}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
                } else set(fieldError, `[${key}][${index}][${fieldKey}]`, '');
              });
            });
          }
          break;
        }
        case 'averageCosting': {
          const data = copyState?.averageCosting?.averageCostingInfo || [];
          data?.forEach((ele, index) => {
            ele?.expandData?.forEach((element, chindex) => {
              const declaredRows = element?.multiQuantityRows as ValidatableRow[] | undefined;
              const multiQuantityRows: ValidatableRow[] = declaredRows?.length ? declaredRows : [{}];
              multiQuantityRows.forEach((row, rowIndex) => {
                fields.forEach((fieldKey) => {
                  if (isFalsyOrEmptyObject(row[fieldKey])) {
                    hasErrors = true;
                    set(fieldError, `[${key}][${index}][${chindex}][${rowIndex}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
                  } else set(fieldError, `[${key}][${index}][${chindex}][${rowIndex}][${fieldKey}]`, '');
                });
              });
            });
          });
          break;
        }
        case 'quantities': {
          const quantitiesData = copyQuantitiesState?.quantities?.quantitiesInfo || [];
          checkArray(quantitiesData) &&
            quantitiesData?.forEach((ele, index) => {
              if (ele?.expandData && ele?.expandData?.length) {
                ele?.expandData?.forEach((element, chindex) => {
                  fields.forEach((fieldKey) => {
                    if (isFalsyOrEmptyObject(element[fieldKey])) {
                      hasErrors = true;
                      set(fieldError, `[${key}][${index}][${chindex}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
                    } else set(fieldError, `[${key}][${index}][${chindex}][${fieldKey}]`, '');
                  });
                });
              }
            });
          break;
        }
        case 'detailedCostingErr': {
          const detailedCostData = copyDetailedState?.detailedCosting?.bomTypeArr || [];
          detailedCostData?.forEach((element, index) => {
            element?.expandData?.forEach((expData, expIndex) => {
              fields.forEach((fieldKey) => {
                if (isFalsyOrEmptyObject(expData[fieldKey])) {
                  hasErrors = true;
                  set(fieldError, `[${key}][${index}][${expIndex}][${fieldKey}]`, THIS_FIELD_IS_REQUIRED);
                } else set(fieldError, `[${key}][${index}][${expIndex}][${fieldKey}]`, '');
              });
            });
          });
          break;
        }
        case 'productTemplate': {
          const selectedTemplate = copyState?.productTemplate;
          if (isEmpty(selectedTemplate)) {
            hasErrors = true;
            fieldError.productTemplate = THIS_FIELD_IS_REQUIRED;
          } else {
            fieldError.productTemplate = '';
          }
          break;
        }
        default:
          break;
      }
    });
    setGlobalErrors((err) => ({ ...err, ...fieldError }));
    return hasErrors;
    /* Exactly the values the body reads. `setGlobalErrors` is a useState setter and is stable, so it
       is not listed. */
  }, [globalError, productCostingstate, additionalState, additionalComponentState, testingState, detailedCostingState, detailedQuanitiesState, colourWayToggle, hideColourCol]);

  /**
   * Clears the error at one path as soon as the user edits that field, so the message disappears
   * without waiting for the next full validation pass.
   */
  const onChangeCheckIfErrorExist = useCallback((path: string, errorsData: GlobalError): void => {
    const error = errorsData;
    set(error, path, '');
    setGlobalErrors((err) => ({ ...err, ...error }));
  }, []);

  return {
    checkValidation,
    setGlobalErrors,
    globalError,
    onChangeCheckIfErrorExist,
  };
}
